"use strict";
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateAnswerStatus = exports.answerToString = exports.getAnswerValue = exports.toBooleanAnswerStatus = exports.toNumberAnswerStatus = exports.toAnswerStatus = exports.isAnswerStatus = exports.answerStatusValues = void 0;
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
exports.answerStatusValues = ['answered', 'dont_know', 'refusal', 'not_applicable'];
/**
 * Non-response values a questionnaire may store in place of an answer, and the
 * status each one stands for. These are the members the lists of values use for
 * the same purpose, so that a choice shared by both kinds of attribute keeps
 * its meaning.
 *
 * This is not the exhaustive list of what a questionnaire may write: it holds
 * the values the surveys known here use, and a survey naming its own
 * non-responses differently maps them in its own parser.
 */
// TODO Normalize `refusal` and `preferNotToAnswer`, which name the same
// non-response and both appear in the lists of values of `GenericAttributes`,
// so that a questionnaire has one way to say it and the exports one value to
// map. Both are accepted here until then.
const statusByNonResponseValue = {
    dontKnow: 'dont_know',
    unknown: 'dont_know',
    preferNotToAnswer: 'refusal',
    refusal: 'refusal',
    nonApplicable: 'not_applicable'
};
/**
 * @param {unknown} value The value to check
 * @returns {boolean} Whether the value is an answer wrapped in a known status
 */
const isAnswerStatus = (value) => typeof value === 'object' && value !== null && exports.answerStatusValues.includes(value.status);
exports.isAnswerStatus = isAnswerStatus;
/**
 * Wrap an answer read from a questionnaire response in its status.
 *
 * Questionnaires store these answers as plain values, so this accepts them as
 * they come: a non-response value known to the surveys above becomes its
 * status, anything else becomes the answered value. The value itself is not
 * converted, as only the survey knows what its own choices mean: a survey whose
 * choices are strings, like `yes` for a boolean attribute, has to map them in
 * its own parser rather than have this guess.
 *
 * A blank response, which the questionnaires write as `undefined`, `null` or an
 * empty string, gives no status at all: it says only that nothing was recorded,
 * not whether the question was ever asked.
 *
 * The value is typed `unknown` rather than `T | string | AnswerStatus<T>`,
 * because it is read from a response that no type guards: a value of the wrong
 * type has to reach the attribute for `validateAnswerStatus` to report it,
 * instead of being dropped here.
 *
 * @param {unknown} value The answer as stored in the response
 * @returns {Optional<AnswerStatus<T>>} The wrapped answer, or `undefined` when
 * the response is blank
 */
const toAnswerStatus = (value) => {
    if ((0, LodashExtensions_1._isBlank)(value)) {
        return undefined;
    }
    if ((0, exports.isAnswerStatus)(value)) {
        return value;
    }
    const nonResponseStatus = typeof value === 'string' ? statusByNonResponseValue[value] : undefined;
    return nonResponseStatus
        ? { status: nonResponseStatus }
        : { status: 'answered', value: value };
};
exports.toAnswerStatus = toAnswerStatus;
/**
 * Wrap an answer to a question asking for a number.
 *
 * The widgets asking for a number store it as a string, next to the choices
 * that are not a number, like `dontKnow`, so a string holding a number becomes
 * the answered number and a known non-response becomes its status. Decimals go
 * through as they are written, since an attribute may well be answered with
 * one, like a number of rooms of 1.5; whether the attribute takes only whole
 * numbers is for its own validator to say.
 *
 * A string has to hold nothing but that number: one followed by anything else
 * is not wrapped, so that `validateAnswerStatus` reports it instead of it
 * going through silently truncated or stored as an answered string.
 *
 * @param {unknown} value The answer as stored in the response
 * @returns {Optional<AnswerStatus<number>>} The wrapped answer, or `undefined`
 * when the value is blank or is a string that is not a number
 */
const toNumberAnswerStatus = (value) => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
            return (0, exports.toAnswerStatus)(Number(trimmed));
        }
        if (!(0, LodashExtensions_1._isBlank)(trimmed) && statusByNonResponseValue[value] === undefined) {
            return undefined;
        }
    }
    return (0, exports.toAnswerStatus)(value);
};
exports.toNumberAnswerStatus = toNumberAnswerStatus;
/**
 * Wrap an answer to a yes/no question.
 *
 * Questionnaires store these as `yes`/`no` or a boolean, next to the
 * non-response choices like `dontKnow`. Those strings become the answered
 * boolean; a known non-response becomes its status.
 *
 * @param {unknown} value The answer as stored in the response
 * @returns {Optional<AnswerStatus<boolean>>} The wrapped answer, or `undefined`
 * when the response is blank
 */
const toBooleanAnswerStatus = (value) => {
    if (value === 'yes' || value === 'no') {
        return (0, exports.toAnswerStatus)(value === 'yes');
    }
    return (0, exports.toAnswerStatus)(value);
};
exports.toBooleanAnswerStatus = toBooleanAnswerStatus;
/**
 * @param {Optional<AnswerStatus<T>>} answer The answer to read
 * @returns {Optional<T>} The value that was answered, or `undefined` when there
 * is none, whatever the reason
 */
const getAnswerValue = (answer) => answer !== undefined && answer.status === 'answered' ? answer.value : undefined;
exports.getAnswerValue = getAnswerValue;
/**
 * Write an answer as a single string, for the displays that show an attribute
 * as it was answered, like the admin review panels. Showing the status of an
 * answer without a value tells a reviewer that the question got an answer,
 * which reading the value alone would not.
 *
 * @param {Optional<AnswerStatus<T>>} answer The answer to display
 * @param {string} [noAnswerLabel] What to display when no answer was recorded
 * @returns {string} The value that was answered, or the status when there is
 * no value
 */
const answerToString = (answer, noAnswerLabel = '?') => answer === undefined ? noAnswerLabel : answer.status === 'answered' ? String(answer.value) : answer.status;
exports.answerToString = answerToString;
/**
 * Validate an answer wrapped in its status, for the `validateParams` of the
 * objects holding one.
 *
 * @param {string} attribute The name of the attribute, for error display
 * @param {unknown} value The value to validate
 * @param {string} displayName The name of the object, for error display
 * @param {Function} validateValue Validator for the answered value itself,
 * like `ParamsValidatorUtils.isBoolean`
 * @returns {Error[]} The errors found, empty when the answer is valid
 */
const validateAnswerStatus = (attribute, value, displayName, validateValue) => {
    if (value === undefined || value === null) {
        return [];
    }
    if (!(0, exports.isAnswerStatus)(value)) {
        return [new Error(`${displayName} validateParams: ${attribute} should be an answer with a status`)];
    }
    if (value.status !== 'answered') {
        // A status other than answered stands for the absence of a value, so a
        // value stored beside it contradicts it, and reading the answer would
        // leave that value unseen
        return (0, LodashExtensions_1._isBlank)(value.value)
            ? []
            : [new Error(`${displayName} validateParams: ${attribute} should have no value when it is not answered`)];
    }
    // An answered status without a value says both that there is an answer and
    // that there is none
    if ((0, LodashExtensions_1._isBlank)(value.value)) {
        return [new Error(`${displayName} validateParams: ${attribute} should have a value when it is answered`)];
    }
    return validateValue(`${attribute}.value`, value.value, displayName);
};
exports.validateAnswerStatus = validateAnswerStatus;
//# sourceMappingURL=AnswerStatus.js.map